import { UIIcon } from "@dropbox/dig-icons";
import {
  AddToCollectionLine,
  Twinkle2Line,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { PAP_Open_DashLink } from "@mirage/analytics/events/types/open_dash_link";
import { StackSortOption } from "@mirage/service-settings/service/types";
import { useStackByIdSortSettings } from "@mirage/service-settings/useStackByIdSortSettings";
import { ButtonWithTooltip } from "@mirage/shared/buttons/ButtonWithTooltip";
import { IconButtonWithTooltip } from "@mirage/shared/icons/IconButtonWithTooltip";
import {
  activeStackHasWritePermissionsAtom,
  activeStackItemShortcutsAtom,
  activeStackSectionsAtom,
} from "@mirage/stacks/atoms";
import { useReportStackPapEvent } from "@mirage/stacks/hooks/useReportStackPapEvent";
import {
  FullScreenStackPreset,
  SortIconMenu,
} from "@mirage/stacks/SortIconMenu";
import i18n from "@mirage/translations";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useAtom, useAtomValue } from "jotai";
import { useNavigate } from "react-router-dom";
import {
  useCreateStackSection,
  useStackChatSources,
} from "../../hooks/FullScreenStackHooks";
import {
  useFetchContentSuggestions,
  useSectionsNavigation,
} from "../../hooks/sectionHooks";
import { AddItemButtonAugRev } from "../Sections/AddItemButton";
import { SearchInStackMenu } from "../Sections/SearchInStack";

import type { stacks } from "@dropbox/api-v2-client";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
// Note: doing this since it seems like other mosaics allow imports from service-conversation already
// eslint-disable-next-line boundaries/element-types
import { PAP_Click_StackChat } from "@mirage/analytics/events/types/click_stack_chat";
import {
  type AskWithMultiDocsParams,
  CtaSource,
} from "@mirage/service-conversation/router_state_types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import type { SelectableStackSortPreference } from "@mirage/service-settings/service/types";
import { useIsPublicSharingAllowed } from "@mirage/service-stack-admin-settings/hooks";
import {
  accessLevelIconSrcForMemberCount,
  descriptionForStackAccessLevel,
} from "@mirage/stacks/ShareModal/Utils";
import { useMemo } from "react";
import { ShareStackButton } from "../ShareStackButton";

export const HeaderWithChatActions = ({
  isNewStack,
  stack,
}: {
  isNewStack: boolean;
  stack: stacks.Stack | null;
}) => {
  const navigate = useNavigate();
  const reportStackPapEvent = useReportStackPapEvent();
  const items = useAtomValue(activeStackItemShortcutsAtom);
  const hasWritePermissions = useAtomValue(activeStackHasWritePermissionsAtom);
  const [{ allSections: sections, defaultSection }] = useAtom(
    activeStackSectionsAtom,
  );
  const { scrollToSection, triggerEditSection } = useSectionsNavigation();
  const createStackSection = useCreateStackSection();
  const isPublicSharingAllowed = useIsPublicSharingAllowed();
  const currentAccount = useDropboxAccount();
  const { hasChatSources, chatSources } = useStackChatSources();

  const { sortPreference, setStackByIdSortPreference } =
    useStackByIdSortSettings(stack?.namespace_id ?? "");

  const { contentSuggestions, isFetchingContentSuggestions } =
    useFetchContentSuggestions();

  const accessLevelIcon = useMemo(
    () =>
      accessLevelIconSrcForMemberCount(
        stack?.sharing_data?.members?.length ?? 0,
        stack?.sharing_data?.shared_link?.access_level,
        stack?.publish_data?.is_published,
        isPublicSharingAllowed,
      ),
    [
      isPublicSharingAllowed,
      stack?.publish_data?.is_published,
      stack?.sharing_data?.members?.length,
      stack?.sharing_data?.shared_link?.access_level,
    ],
  );

  const shareButtonTooltip = isNewStack
    ? i18n.t("new_stack_share_button_tooltip")
    : descriptionForStackAccessLevel(
        stack?.sharing_data?.shared_link?.access_level,
        stack?.sharing_data?.shared_link?.team_name,
        isPublicSharingAllowed,
      );

  const handleAddSection = async () => {
    if (!stack?.namespace_id || !sections) return;

    // Create new section with default name
    const newSectionId = await createStackSection(i18n.t("new_section_name"));
    if (!newSectionId) return;

    // Scroll to the new section after a short delay to ensure it's rendered
    scrollToSection(newSectionId);

    // Set the new section as current and enter edit mode
    triggerEditSection(newSectionId);
  };

  const handleChatClick = () => {
    if (!stack?.sharing_id) return;

    reportStackPapEvent(
      PAP_Click_StackChat({
        actionSurfaceComponent: "stack_header",
      }),
    );

    const state = buildConversationRouterState(CtaSource.StackChat, {
      chatSources,
      scenarioMeta: {
        type: "stack",
        sharingId: stack.sharing_id,
        namespaceId: stack?.namespace_id || "",
      },
    } satisfies AskWithMultiDocsParams);

    navigate(RoutePath.CHAT, {
      state,
    });
  };

  return (
    <>
      {hasWritePermissions && defaultSection && (
        <AddItemButtonAugRev
          contentSuggestions={contentSuggestions}
          emphasize={!items || items.length === 0}
          loadingSuggestions={isFetchingContentSuggestions}
          section={defaultSection}
          variant="primary"
        >
          {i18n.t("add_stack_items_button_text")}
        </AddItemButtonAugRev>
      )}
      <ShareStackButton
        stack={stack}
        disabled={!currentAccount || isNewStack}
        iconType="custom"
        icon={accessLevelIcon}
        tooltip={shareButtonTooltip}
      />
      <ButtonWithTooltip
        variant="opacity"
        withIconStart={<UIIcon src={Twinkle2Line} />}
        disabled={isNewStack || !hasChatSources}
        onClick={handleChatClick}
        tooltipProps={{
          title: !hasChatSources
            ? i18n.t("no_documents_to_summarize")
            : undefined,
        }}
      >
        {i18n.t("ask")}
      </ButtonWithTooltip>
      {!isNewStack && (
        <>
          <SortIconMenu
            augustRevision={false}
            sortPreference={sortPreference as SelectableStackSortPreference}
            onSortSelect={setStackByIdSortPreference}
            optionsPreset={FullScreenStackPreset}
            defaultOption={{
              option: StackSortOption.SECTION,
            }}
            showDropdownIcon={false}
          />
          <SearchInStackMenu
            // Force re-render when stack changes
            key={stack?.namespace_id}
            items={items}
            onOpenLink={() => {
              reportStackPapEvent(PAP_Open_DashLink());
            }}
          />
          <IconButtonWithTooltip
            variant="borderless"
            tooltipProps={{
              title: i18n.t("add_section"),
            }}
            onClick={handleAddSection}
          >
            <UIIcon src={AddToCollectionLine} />
          </IconButtonWithTooltip>
        </>
      )}
    </>
  );
};
